import { convertISODateToMMDDYYYY, convertISOToTime, displayFullName, formatCurrency } from '@/lib/utils';
import { Download, FileText } from 'lucide-react';
import { type InvoiceResponse } from '@/types/invoice.types';
import { useNavigate } from 'react-router-dom';
import { EmailLogStatusLabel, invoiceStatusBadgeDetails } from '@/utils/constant';
import { downloadInvoicePDF } from '@/services/invoiceService';
import { InvoiceStatusBadge } from '@pages/caseManagement/invoice/InvoiceView';
import { EmailStatus, type EmailLogResponse } from '@/types/types';
import KeyValueListDisplay from '@/components/KeyValueDisplay';

export const BillingDetailsPeContent = ({
  billingData,
  workOrderId,
}: {
  billingData: InvoiceResponse | null | undefined;
  workOrderId?: string;
}) => {
  const status = billingData?.status;
  const emailLogs = billingData?.emailLogs ?? [];
  const totalAmount = billingData?.invoiceBillingItems?.reduce((total, item) => total + (item.amount ?? 0), 0) ?? 0;
  const navigate = useNavigate();
  return (
    <div className="flex flex-col gap-6 w-full px-6 py-6 text-sm ">
      {emailLogs.length ? (
        <div className="flex flex-col gap-3 w-full">
          <span className="text-primary font-semibold">Emails Sent</span>
          <KeyValueListDisplay
            data={emailLogs.map((log: EmailLogResponse) => ({
              label: convertISODateToMMDDYYYY(log.sentAt) + ' ' + convertISOToTime(log.sentAt),
              value: EmailLogStatusLabel[log.status],
              valueClassName: log.status === EmailStatus.SENT ? 'text-green-700' : 'text-error',
            }))}
          />
        </div>
      ) : null}
      {billingData ? (
        <div
          className="flex flex-row gap-2 justify-between p-4 rounded-lg bg-[#F5F5F5] cursor-pointer"
          onClick={() => {
            navigate(
              `/process/invoice/${billingData?.invoiceNumber}${workOrderId ? `?workOrderId=${workOrderId}` : ''}`
            );
          }}
        >
          <FileText className="w-6 h-6 text-primary" />
          <div className="flex flex-col gap-2">
            <span className="font-medium text-sm text-primary">{billingData?.invoiceNumber}</span>
            <span className="text-xs text-placeholder">{`Updated: ${convertISODateToMMDDYYYY(billingData?.updatedAt)} ${convertISOToTime(billingData?.updatedAt)} by ${displayFullName(billingData?.updatedBy ?? {})}`}</span>
            <div className="font-medium">
              <span className="text-placeholder">Amount: </span>
              <span className="text-primary">{formatCurrency(totalAmount)}</span>{' '}
            </div>
          </div>
          <div className="flex flex-col justify-between gap-2">
            <InvoiceStatusBadge key={status} status={invoiceStatusBadgeDetails[status ?? 'DRAFT']} />
            <div className="flex items-end justify-end">
              <Download
                className="h-5 w-5 text-primary mr-4 cursor-pointer"
                onClick={async (e) => {
                  e.stopPropagation();
                  await downloadInvoicePDF(billingData?.invoiceNumber);
                }}
              />
            </div>
          </div>
        </div>
      ) : (
        <div className="w-full justify-center flex font-medium text-sm">No Billing Data</div>
      )}
    </div>
  );
};
